import {
  DATE_TIME_FORMAT,
  MAX,
  MAX_RETURN,
  MAX_SHARPE,
  MIN,
  MIN_VOLATILITY,
  RESERVE_TICKER
} from "./constants";
import moment from "moment";

export function getBaseURL(isAuth, isOptimizer, isPlain) {
  const env = process.env.VUE_APP_ENV;
  const route = isPlain
    ? ""
    : isAuth
    ? "/rest-auth"
    : isOptimizer
    ? "/optimization"
    : "/api";
  const url =
    env === "dev"
      ? "https://dev.analyticalplatform.com"
      : env === "test"
      ? "https://test.analyticalplatform.com"
      : env === "live"
      ? "https://app.analyticalplatform.com"
      : env === "local"
      ? "http://localhost"
      : env;

  return `${url}${route}`;
}

export function getParsedObject(object) {
  if (object) {
    return JSON.parse(JSON.stringify(object));
  }
  return [];
}

export function filterByReference(arr1, arr2) {
  let res = [];
  res = arr1.filter(el => {
    return !arr2.find(element => {
      return element.id === el.id;
    });
  });
  return res;
}

export function getMyReportsDataFormat(item) {
  return {
    id: item.id,
    valid_from: item.valid_from,
    state: item.state,
    type: item.type,
    profit: item.profit,
    expiration: item.expiration,
    next_rebalance: item.next_rebalance
  };
}

//report privileges access
export const formatDateToCommon = date =>
  moment(date).format(DATE_TIME_FORMAT());

export function transformTimeSlotProps(props) {
  const formattedProps = {};
  Object.entries(props).forEach(([key, value]) => {
    formattedProps[key] =
      value < 10 && key !== "totalDays" ? `0${value}` : String(value);
  });

  return formattedProps;
}

export const strategyApiTranslator = () => {
  return "mock";
};

export const checkWrongMinMax = (data, oppositeLimit) => {
  return (
    (oppositeLimit &&
      oppositeLimit.limit.value === MIN &&
      data.limit.value === MAX &&
      oppositeLimit.value > data.value) ||
    (oppositeLimit &&
      oppositeLimit.limit.value === MAX &&
      data.limit.value === MIN &&
      oppositeLimit.value < data.value)
  );
};

export const checkWrongWeightSum = (data, rules) => {
  const maxRule = rules.find(rule => rule.rule.value === "count");
  const maxCount = maxRule ? maxRule.value : 100;
  const perStockLimit = 100 / maxCount;
  return (
    (data.limit.value === MAX && data.value <= perStockLimit) ||
    (data.limit.value === MIN && data.value >= perStockLimit)
  );
};

export const composeRemovedTickers = (
  portfolio,
  removedTickers,
  blockedTickers
) => {
  const input = portfolio.portfolio_input.custom_portfolio;
  const transformedRemovedTickers = {};
  const custom_portfolio = input.map(({ ticker }) => ticker);
  if (portfolio) {
    [...custom_portfolio, RESERVE_TICKER].forEach(ticker => {
      transformedRemovedTickers[ticker] = {
        weights: {
          equal: null,
          min: null,
          max: null
        }
      };
    });

    if (blockedTickers.length) {
      blockedTickers.forEach(ticker => {
        transformedRemovedTickers[ticker] = {
          weights: {
            equal: 0,
            min: null,
            max: null
          }
        };
      });
    }

    if (removedTickers.length) {
      [...custom_portfolio, RESERVE_TICKER].forEach(ticker => {
        let equal = transformedRemovedTickers[ticker].weights.equal;
        if (custom_portfolio.includes(ticker)) {
          const item = input.find(item => item.ticker === ticker);
          equal = removedTickers.includes(item.ticker) ? 0 : item.weight;
        }
        transformedRemovedTickers[ticker] = {
          weights: {
            equal: equal,
            min: null,
            max: null
          }
        };
      });
    }
    return JSON.stringify(transformedRemovedTickers);
  } else {
    return null;
  }
};

export const composeGlobalLimits = (stringify, rules, poolId) => {
  if (rules.length > 0) {
    const withRules = {
      pool_id: poolId,
      weights: {
        min:
          rules.find(
            rule => rule.rule.value === "weight" && rule.limit.value === MIN
          )?.value / 100 || null,
        max:
          rules.find(
            rule => rule.rule.value === "weight" && rule.limit.value === MAX
          )?.value / 100 || null
      },
      count: rules.find(rule => rule.rule.value === "count")?.value
    };
    return stringify ? JSON.stringify(withRules) : withRules;
  } else {
    const noRules = {
      pool_id: poolId
    };
    return stringify ? JSON.stringify(noRules) : noRules;
  }
};
export const composeTickerRuleList = data => {
  const list = [];
  const blocked = data.filter(rule => rule.rule.value === "tickers");
  blocked.forEach(el => list.push(el.value.ticker));
  return list;
};

export const mapPortfolioToArray = input => {
  return Object.entries(input).map(([ticker, val]) => ({
    ticker,
    ...val
  }));
};

export const filterObject = (obj, condition) => {
  return Object.keys(obj)
    .filter(key => condition(obj[key]))
    .reduce((res, key) => ((res[key] = obj[key]), res), {});
};

export const reverseMapPortfolio = input => {
  if (!Array.isArray(input)) {
    return input;
  } else {
    const portfolio = {};
    input.forEach(entry => {
      portfolio[entry.ticker] = {
        ...entry
      };
      delete portfolio[entry.ticker].ticker;
    });
    return portfolio;
  }
};

export const mapWeightsToArray = input => {
  return Object.entries(input).map(([ticker, weight]) => ({
    ticker,
    weight
  }));
};

export const mapTickerIcons = input => {
  return Object.entries(input).map(([ticker, icon]) => ({
    ticker,
    icon
  }));
};

export const checkIfMissingInPool = (pool, tickers) => {
  return !tickers.every(ticker => pool.includes(ticker));
};

export const formatToPercents = (input, fixedDecimals) => {
  const out = fixedDecimals ? (input * 100).toFixed(1) : parseInt(input * 100);
  return `${out} %`;
};

export const compareObjects = (object1, object2) => {
  return JSON.stringify(object1) === JSON.stringify(object2);
};

export const prepareForConversion = (obj, constant) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => key.includes(constant))
  );
};

export const numberToInt = number => {
  let i = 1;
  let result = number;
  while (result % 1 != 0) {
    i = i * 10;
    result = number * i;
  }
  return { number: result, times: i };
};

export const multiply100 = number => {
  const data = numberToInt(number);
  return data.number / (data.times / 100);
};

export const getObjectiveValue = (objective, data) => {
  switch (objective) {
    case MIN_VOLATILITY: {
      return multiply100(data.volatility).toString() + " %";
    }
    case MAX_RETURN: {
      return multiply100(data.expected_return).toString() + " %";
    }
    case MAX_SHARPE: {
      return data.sharpe;
    }
    default:
      return null;
  }
};

export function getFactorInvestingURL() {
  const env = process.env.VUE_APP_ENV;
  const url = env === "dev"
    ? "https://dev.analyticalplatform.com"
    : env === "test"
    ? "https://test.factors.analyticalplatform.com"
    : env === "live"
    ? "https://factors.analyticalplatform.com"
    : env === "local"
    ? "http://localhost:3000/"
    : env;

  return url;
}
